import React, { useState, useEffect, useRef } from "react";
import DragDrop from "../../DragDrop";
import { Copy, Pencil, Trash, WarningCircle } from "@phosphor-icons/react";
import axiosInstance from "../../../api/axiosInstance";
import PageLoader from "../../loader/PageLoader";
import ImageLoader from "../../loader/ImageLoader";
import {toast} from "react-toastify";
import { uploadImageToServer } from "../../../api/uploadImage";

const AddRoom = ({ onComplete }) => {
    const [formData, setFormData] = useState({
        id: null,
        number: "",
        type: "",
        capacity: "",
        floor: "",
        photos: [],
    });
    const [showForm, setShowForm] = useState(false);
    const [rooms, setRooms] = useState([]);
    const [uploadedImageKeys, setUploadedImageKeys] = useState([]);
    const [uploadedImages, setUploadedImages] = useState([]);
    const hotelId = localStorage.getItem("hotelId");
    const [newUser, setNewUser] = useState(false);
    const [selectedRoom, setSelectedRoom] = useState(null);
    const [isEditMode, setIsEditMode] = useState(false);
    const [removedImageKeys, setRemovedImageKeys] = useState([]);
    const [isPageLoading, setIsPageLoading] = useState(true);
    const [errors, setErrors] = useState({});
    const [duplicatedFrom, setDuplicatedFrom] = useState(null);
    const numberInputRef = useRef(null);

    // The API returns rooms in insertion order and new ones land at the end, so
    // the list is sorted for display. Handlers index into this same array, which
    // is why the state itself is sorted rather than a copy at render time.
    const sortByRoomNumber = (roomList) =>
        [...roomList].sort(
            (a, b) => (Number(a.number) || 0) - (Number(b.number) || 0),
        );

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData((prevFormData) => ({ ...prevFormData, [name]: value }));
        setErrors((prev) => ({ ...prev, [name]: "" }));
    };

    // A room number identifies one room in the hotel, so it cannot repeat. The room
    // being edited is skipped, otherwise saving it unchanged clashes with itself.
    const isRoomNumberTaken = (number) =>
        rooms.some(
            (room) =>
                String(room.number) === String(number) && room.id !== formData.id,
        );

    const validateForm = () => {
        const newErrors = {};
        if (!formData.number) newErrors.number = "Room number is required";
        else if (isRoomNumberTaken(formData.number))
            newErrors.number = "This room number is already added";
        if (!formData.type) newErrors.type = "Room type is required";
        if (!formData.capacity) newErrors.capacity = "Max capacity is required";
        if (!formData.floor) newErrors.floor = "Floor number is required";
        // if (uploadedImages.length === 0)
        //     newErrors.photos = "At least one image is required";
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    useEffect(() => {
        // Fetch existing photos when the component mounts
        const fetchRooms = async () => {
            try {
                const response = await axiosInstance.get(
                    `/api/v1/hotels/${hotelId}/rooms`,
                );

                const existingRoomDetails = response.data.data || [];
                setRooms(sortByRoomNumber(existingRoomDetails));
                const existingImages =
                    response.data.data.map((data) => data.photos) || [];
                fetchFloorPlanImages(existingImages);
                // Fetch URLs for existing photos
                // console.log(
                //     "fetch data",
                //     response.data.data.map((data) => data.photos),
                // );

                if (existingRoomDetails.length > 0) {
                    setNewUser(false);
                }
                setIsPageLoading(false);
                // fetchFloorPlanImages(existingPhotos);
            } catch (error) {
                console.error("Failed to fetch hotel photos:", error);
                setIsPageLoading(false);
            }
        };

        fetchRooms();
    }, [hotelId]);

    const resetFormStates = () => {
        setFormData({
            id: null,
            number: "",
            type: "",
            capacity: "",
            floor: "",
            photos: [],
        });
        setUploadedImageKeys([]);
        setUploadedImages([]);
        setRemovedImageKeys([]);
        setIsEditMode(false);
        setSelectedRoom(null);
        setDuplicatedFrom(null);
        setErrors({});
    };

    const fetchFloorPlanImages = async (imageKeys = []) => {
        const imagePromises = imageKeys.map(async (key) => {
            try {
                const response = await axiosInstance.get(
                    `/api/v1/images/download-presigned-url?key=${key}`,
                );
                const imageUrl = response.data.url;
                return { key, url: imageUrl };
            } catch (error) {
                console.error("Error fetching image URL:", error);
                return null;
            }
        });

        const images = await Promise.all(imagePromises);
        setUploadedImages(images.filter((img) => img !== null));
    };

    const handleSaveRoom = async (e) => {
        e.preventDefault();
        if (!validateForm()) return;
        // Check for empty required fields
        const isEmpty = Object.values(formData).some((field) => field === "");
        if (isEmpty) {
            alert("Please fill in all required fields.");
            return;
        }

        try {
            // console.log("uploaded image keys", uploadedImageKeys);
            const allPhotos = [
                ...formData.photos,
                ...uploadedImageKeys.map((key) => key),
            ];
            // console.log("allphotos", allPhotos);
            // Existing photos come back from the API as plain keys, freshly
            // uploaded ones as { key }. A duplicated room carries the former.
            const photoKeys = allPhotos
                .map((photo) => (typeof photo === "string" ? photo : photo.key))
                .filter((key) => !removedImageKeys.includes(key));
            // console.log("-----------", photoKeys);

            // Create the final form data
            const finalFormData = {
                ...formData,
                capacity: parseInt(formData.capacity),
                floor: parseInt(formData.floor),
                number: parseInt(formData.number),
                photos: photoKeys, // Replace photos array with only keys
            };

            let response;
            if (newUser) {
                response = await axiosInstance.post(
                    `/api/v1/hotels/${hotelId}/rooms-info`,
                    finalFormData,
                );
            } else {
                response = await axiosInstance.post(
                    `/api/v1/hotels/${hotelId}/rooms`,
                    finalFormData,
                );
            }

            // console.log("Room creation successful", response);

            // Update the rooms array with the new room, including the ID from the response
            const newRoom = { ...finalFormData, id: response.data.id };
            setRooms(sortByRoomNumber([...rooms, newRoom]));

            resetFormStates();
            setShowForm(false);
            setNewUser(false);
        } catch (error) {
            toast.error(error?.response?.data?.message || "Some Error Occured")
            console.error("Failed to save room data:", error);
        }
    };

    const handleEditClick = (index) => {
        resetFormStates();
        const selectedRoom = rooms[index];
        // console.log("selected room", selectedRoom);
        setFormData({ ...selectedRoom, id: selectedRoom.id }); // Ensure ID is included
        setShowForm(true);
        setIsEditMode(true);
        setSelectedRoom(selectedRoom);
        setRemovedImageKeys([]);
        fetchFloorPlanImages(selectedRoom.photos);
    };

    // Most rooms on a floor differ only by number, so copying one and typing the
    // new number is far quicker than filling the form again. Everything but the
    // number carries over, including the photos.
    const handleDuplicateClick = (index) => {
        resetFormStates();
        const sourceRoom = rooms[index];
        setFormData({
            id: null,
            number: "",
            type: sourceRoom.type,
            capacity: sourceRoom.capacity,
            floor: sourceRoom.floor,
            photos: sourceRoom.photos || [],
        });
        setShowForm(true);
        setIsEditMode(false);
        setDuplicatedFrom(sourceRoom.number);
        fetchFloorPlanImages(sourceRoom.photos || []);
        setTimeout(() => numberInputRef.current?.focus(), 0);
    };

    const handleFileUpload = async (files) => {
        const fileArray = Array.from(files);
        // console.log("Files to upload:", fileArray);

        // Add placeholder images with loading state
        const placeholders = fileArray.map((file, index) => ({
            id: `temp_${Date.now()}_${index}`,
            isLoading: true,
            file: file,
        }));

        setUploadedImages((prevImages) => [...prevImages, ...placeholders]);

        const uploadPromises = placeholders.map(async (placeholder, index) => {
            try {
                // console.log(
                //     `Starting upload for file ${index}:`,
                //     placeholder.file.name,
                // );
                const uploadUrlResponse = await axiosInstance.get(
                    `/api/v1/images/upload-presigned-url?file_name=${placeholder.file.name}`,
                );
                const { key, url } = uploadUrlResponse.data;
                await uploadImageToServer(placeholder.file, url);
                // console.log(
                //     `Upload completed for file ${index}:`,
                //     placeholder.file.name,
                // );

                return {
                    id: placeholder.id,
                    key,
                    url: URL.createObjectURL(placeholder.file),
                    isLoading: false,
                };
            } catch (error) {
                console.error(`Error uploading image ${index}:`, error);
                return { ...placeholder, isLoading: false, error: true };
            }
        });

        const uploadedFiles = await Promise.all(uploadPromises);

        setUploadedImages((prevImages) =>
            prevImages.map(
                (img) => uploadedFiles.find((u) => u.id === img.id) || img,
            ),
        );

        const validFiles = uploadedFiles.filter((file) => !file.error);
        setUploadedImageKeys((prevKeys) => [
            ...prevKeys,
            ...validFiles.map((f) => ({ key: f.key })),
        ]);
        setErrors((prev) => ({ ...prev, photos: "" }));
    };

    const handleRemoveMediaFiles = (indexToRemove) => {
        setUploadedImages((prevImages) => {
            const newImages = prevImages.filter(
                (_, index) => index !== indexToRemove,
            );
            const removedImage = prevImages[indexToRemove];
            if (
                removedImage &&
                !removedImage.isLoading &&
                !removedImage.error
            ) {
                setRemovedImageKeys((prev) => [...prev, removedImage.key]);
            }
            return newImages;
        });
        setUploadedImageKeys((prevKeys) =>
            prevKeys.filter((_, index) => index !== indexToRemove),
        );
    };

    const trimImgName = (name) => {
        return name && name.length > 12
            ? name.substring(0, 12) + "..."
            : name || "";
    };

    const handleDeleteClick = async (index) => {
        try {
            const updatedRooms = rooms.filter((_, i) => i !== index);
            setRooms(updatedRooms);
            const roomId = rooms[index].id;
            const response = await axiosInstance.delete(
                `/api/v1/hotels/${hotelId}/rooms/${roomId}`,
            );
            // console.log("Room deleted", response.data);
        } catch (error) {
            console.log("error while deleting the room", error);
        }
    };

    const hasChanges = () => {
        if (isEditMode) {
            if (!selectedRoom) return false;
            return (
                Object.keys(formData).some(
                    (key) => formData[key] !== selectedRoom[key],
                ) ||
                uploadedImageKeys.length > 0 ||
                removedImageKeys.length > 0
            );
        } else {
            // For new room, check if any required field is filled or any image is uploaded
            return (
                formData.number !== "" ||
                formData.type !== "" ||
                formData.capacity !== "" ||
                formData.floor !== "" ||
                uploadedImageKeys.length > 0
            );
        }
    };

    const handleUpdateRoom = async (e) => {
        e.preventDefault();
        if (!validateForm()) return;
        try {
            const updatedFormData = {
                ...formData,
                capacity: parseInt(formData.capacity),
                floor: parseInt(formData.floor),
                number: parseInt(formData.number),
                photos: [
                    ...formData.photos.filter(
                        (photo) => !removedImageKeys.includes(photo),
                    ),
                    ...uploadedImageKeys.map((photo) => photo.key),
                ],
            };

            // console.log("selected room id", selectedRoom.id);

            const response = await axiosInstance.put(
                `/api/v1/hotels/${hotelId}/rooms/${formData.id}`,
                updatedFormData,
            );

            // console.log("Room updated successfully", response);

            // Update the rooms array
            setRooms(
                sortByRoomNumber(
                    rooms.map((room) =>
                        room.id === selectedRoom.id ? updatedFormData : room,
                    ),
                ),
            );

            resetFormStates();
            setShowForm(false);
        } catch (error) {
            toast.error(error?.response?.data?.message || "Some Error Occured")
            console.error("Failed to update room:", error);
        }
    };

    const handleNextClick = () => {
        if (rooms.length > 0) {
            onComplete();
        }
    };
    const handleImageLoad = (index) => {
        setUploadedImages((prevImages) =>
            prevImages.map((img, i) =>
                i === index ? { ...img, isLoading: false } : img,
            ),
        );
    };

    return (
        <>
            {isPageLoading && <PageLoader />}
            <div className="w-full flex">
                <div className="w-1/2 border-r-[1.5px]">
                    <button
                        className={`h-[48px] ${
                            showForm
                                ? "bg-[#FFF2F2] text-[#FFBFC1] cursor-not-allowed"
                                : "bg-[#ff7378]"
                        } px-4 rounded-lg text-[16px] mb-3`}
                        onClick={() => {
                            setShowForm(true);
                            resetFormStates();
                        }}
                        disabled={showForm}
                    >
                        Add Room
                    </button>

                    <div className="flex flex-wrap w-full gap-2">
                        {rooms.length > 0
                            ? rooms.map((data, index) => (
                                  <div
                                      className="border-[1.5px] rounded-lg border-[#BFC4CC] bg-[#EEF0FB] h-[118px] flex flex-col px-2 py-1 mb-2 justify-between w-[48%]"
                                      key={index}
                                  >
                                      <div>
                                          <p className="font-bold text-lg">
                                              {data.number}
                                          </p>
                                          <p className="text-[16px] font-medium">
                                              {data.type}
                                          </p>
                                          <p className="text-[11px]">
                                              {data.capacity} guests
                                          </p>
                                      </div>
                                      <div className="flex gap-1 justify-end">
                                          <button
                                              className="rounded-md p-1 border-[1.5px] border-[#BFC4CC] h-[24px] w-[24px] bg-white"
                                              title="Duplicate room"
                                              onClick={() =>
                                                  handleDuplicateClick(index)
                                              }
                                          >
                                              <Copy size={14} />
                                          </button>
                                          <button
                                              className="rounded-md p-1 border-[1.5px] border-[#F5CCD3] h-[24px] w-[24px] bg-white"
                                              onClick={() =>
                                                  handleDeleteClick(index)
                                              }
                                          >
                                              <Trash
                                                  size={14}
                                                  className="text-[#ff7378]"
                                              />
                                          </button>
                                          <button
                                              className="rounded-md p-1 border-[1.5px] border-[#BFC4CC] h-[24px] w-[24px] bg-white"
                                              onClick={() =>
                                                  handleEditClick(index)
                                              }
                                          >
                                              <Pencil size={14} />
                                          </button>
                                      </div>
                                  </div>
                              ))
                            : ""}
                    </div>
                    <button
                        className="mt-6 px-4 py-2 bg-[#0B22B2] text-white rounded-md absolute bottom-3 left-[19%]"
                        onClick={handleNextClick}
                    >
                        Next
                    </button>
                </div>

                {showForm && (
                    <div className="w-1/2 px-4">
                        <form
                            onSubmit={
                                isEditMode ? handleUpdateRoom : handleSaveRoom
                            }
                        >
                            <div className="flex items-center gap-2">
                                {" "}
                                {isEditMode && (
                                    <button
                                        className="h-[48px] border-[1.5px] border-gray-200  rounded-lg text-[16px] text-[#ff7378] px-4 w-fit"
                                        type="button"
                                        onClick={() => {
                                            setShowForm(false);
                                            resetFormStates();
                                        }}
                                    >
                                        Cancel
                                    </button>
                                )}
                                <button
                                    className={`h-[48px] w-full rounded-lg text-[16px] ${
                                        hasChanges()
                                            ? "bg-[#ff7378]"
                                            : "bg-[#FFF2F2] text-[#FFBFC1] cursor-not-allowed"
                                    }`}
                                    type="submit"
                                    disabled={!hasChanges()}
                                >
                                    {isEditMode ? "Update Room" : "Save Room"}
                                </button>
                            </div>
                            <div className="flex flex-col gap-2 my-3">
                                <div>
                                    <p>Room Number</p>
                                    {duplicatedFrom !== null && (
                                        <p className="text-[11px] text-[#8C9199]">
                                            Copied from room {duplicatedFrom} —
                                            enter a new room number
                                        </p>
                                    )}
                                    <input
                                        type="number"
                                        placeholder="Enter room number"
                                        className="w-[70%] p-2 border-[1.5px] rounded-lg mt-1"
                                        name="number"
                                        ref={numberInputRef}
                                        value={formData.number}
                                        onChange={handleChange}
                                    />
                                    {errors.number && (
                                        <span className="text-[#E47186] text-xs flex items-center gap-1">
                                            <WarningCircle className="text-sm" />
                                            {errors.number}
                                        </span>
                                    )}
                                </div>
                                <div>
                                    <p>Type</p>
                                    <input
                                        type="text"
                                        placeholder="Enter room type"
                                        className="w-[70%] p-2 border-[1.5px] rounded-lg mt-1"
                                        name="type"
                                        value={formData.type}
                                        onChange={handleChange}
                                    />
                                    {errors.type && (
                                        <span className="text-[#E47186] text-xs flex items-center gap-1">
                                            <WarningCircle className="text-sm" />
                                            {errors.type}
                                        </span>
                                    )}
                                </div>
                                <div>
                                    <p>Max Capacity</p>
                                    <input
                                        type="number"
                                        placeholder="Enter max capacity"
                                        className="w-[70%] p-2 border-[1.5px] rounded-lg mt-1"
                                        name="capacity"
                                        value={formData.capacity}
                                        onChange={handleChange}
                                    />
                                    {errors.capacity && (
                                        <span className="text-[#E47186] text-xs flex items-center gap-1">
                                            <WarningCircle className="text-sm" />
                                            {errors.capacity}
                                        </span>
                                    )}
                                </div>
                                <div>
                                    <p>Floor Number</p>
                                    <input
                                        type="number"
                                        placeholder="Enter floor number"
                                        className="w-[70%] p-2 border-[1.5px] rounded-lg mt-1"
                                        name="floor"
                                        value={formData.floor}
                                        onChange={handleChange}
                                    />
                                    {errors.floor && (
                                        <span className="text-[#E47186] text-xs flex items-center gap-1">
                                            <WarningCircle className="text-sm" />
                                            {errors.floor}
                                        </span>
                                    )}
                                </div>
                                <div>
                                    <p>Upload Media</p>
                                    <div>
                                        <DragDrop
                                            onFilesUploaded={handleFileUpload}
                                            uploadedImages={uploadedImages}
                                            onRemoveMediaFile={
                                                handleRemoveMediaFiles
                                            }
                                            acceptedFileTypes={{
                                                "image/*": [
                                                    ".jpeg",
                                                    ".png",
                                                    ".jpg",
                                                ],
                                            }}
                                            maxFiles={10}
                                            dragDropText="Drag and drop room images here"
                                        />
                                    </div>
                                    {errors.photos && (
                                        <span className="text-[#E47186] text-xs flex items-center gap-1">
                                            <WarningCircle className="text-sm" />
                                            {errors.photos}
                                        </span>
                                    )}
                                    <div>
                                        {uploadedImages.length > 0 && (
                                            <div className="flex gap-2 w-full mt-2 h-full flex-nowrap overflow-x-auto">
                                                {uploadedImages.map(
                                                    (photo, index) => (
                                                        <div
                                                            key={photo.id}
                                                            className="w-[158px] h-[142px] rounded-lg border-[1.5px] border-[#BFC4CC] flex-shrink-0 overflow-hidden"
                                                        >
                                                            {photo.isLoading ? (
                                                                <ImageLoader />
                                                            ) : photo.error ? (
                                                                <div className="w-full h-full flex items-center justify-center text-red-500">
                                                                    Upload
                                                                    failed
                                                                </div>
                                                            ) : (
                                                                <>
                                                                    <img
                                                                        src={
                                                                            photo.url
                                                                        }
                                                                        alt={`Room ${
                                                                            index +
                                                                            1
                                                                        }`}
                                                                        className="w-full h-[110px] bg-cover"
                                                                        onLoad={
                                                                            handleImageLoad
                                                                        }
                                                                    />
                                                                    <div className="flex justify-between items-center h-[32px] px-1 bg-[#EEF0FB]">
                                                                        <p>
                                                                            {trimImgName(
                                                                                photo.key,
                                                                            )}
                                                                        </p>
                                                                        <button
                                                                            className="rounded-md p-1 border-[1.5px] border-[#F8D9DE] text-[#DC4662] h-[24px] w-[24px] bg-white"
                                                                            onClick={() =>
                                                                                handleRemoveMediaFiles(
                                                                                    index,
                                                                                )
                                                                            }
                                                                            type="button"
                                                                        >
                                                                            <Trash
                                                                                size={
                                                                                    14
                                                                                }
                                                                            />
                                                                        </button>
                                                                    </div>
                                                                </>
                                                            )}
                                                        </div>
                                                    ),
                                                )}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </form>
                    </div>
                )}
            </div>
        </>
    );
};

export default AddRoom;
